/**
 * Channels Component
 * Hub de canais conectados ao usuário (omnichannel).
 * Segue design system MA Platform — DESIGN_SYSTEM.md.
 */

import React, { useEffect, useState, useRef, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import NumberFormat from "react-number-format";
import {
  IconBrandWhatsapp,
  IconBrandInstagram,
  IconBrandMessenger,
  IconWorld,
  IconTrash,
  IconRobot,
  IconPlayerPause,
  IconPlayerPlay,
  IconArrowRight,
  IconClock,
  IconX,
  IconPlug,
  IconLock,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import AlertResponses from "../../jsx/components/Alert";
import UserContext from "../../contexts/UserContext";

import {
  DeleteAccount,
  GetAccounts,
  PauseOrActiveAccount,
  ConnectWhatsappAccount,
  CheckAccountStatus,
} from "../../services/WaBottyServices";
import WhatsappConnect from "../../jsx/components/WhatsAppChat/WhatsappConnect";
import WhatsAppCloudApiService from "../WhatsAppCloudApi/services/WhatsAppCloudApiService";
import InstagramApiService from "../InstagramConfig/services/InstagramApiService";
import MessengerApiService from "../MessengerConfig/services/MessengerApiService";
import AgentsFlowService from "../AgentsFlow/services/AgentsFlowService";
import "./Channels.css";

// ============================================================
// THEME — paleta por canal
// ============================================================

const CHANNEL_THEME = {
  whatsapp: {
    label: "WhatsApp",
    description: "Chat e mensagens",
    color: "#25D366",
    iconBg: "linear-gradient(135deg, #d4f5e0 0%, #a7e8bf 100%)",
    Icon: IconBrandWhatsapp,
  },
  instagram: {
    label: "Instagram",
    description: "DMs",
    color: "#E4405F",
    iconBg: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)",
    Icon: IconBrandInstagram,
  },
  messenger: {
    label: "Messenger",
    description: "Mensagens via Facebook",
    color: "#0084FF",
    iconBg: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    Icon: IconBrandMessenger,
  },
  website: {
    label: "Website Widget",
    description: "Chat embutido no seu site",
    color: "#475569",
    iconBg: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
    Icon: IconWorld,
  },
};

// Canais que dependem de App Review da Meta. Enquanto não aprovado em produção,
// liberados apenas para accounts autorizadas (whitelist abaixo).
const META_RESTRICTED_CHANNELS = ['instagram', 'messenger'];
const META_ALLOWED_ACCOUNTS = new Set([7, 29]);

// Status WAHA → label/variant
const WAHA_STATUS = {
  WORKING: { label: "Conectado", variant: "success" },
  STARTING: { label: "Iniciando", variant: "info" },
  SCAN_QR_CODE: { label: "Aguardando QR", variant: "warning" },
  STOPPED: { label: "Parado", variant: "neutral" },
  FAILED: { label: "Falha", variant: "danger" },
};

// ============================================================
// SUB-COMPONENT: ConnectedChannelCard
// ============================================================

const ConnectedChannelCard = ({
  channel,
  name,
  avatarUrl,
  statusLabel,
  statusVariant,
  isPaused,
  isMutating,
  canPause,
  agentId,
  agentName,
  subtitle,
  onTogglePause,
  onDisconnect,
}) => {
  const navigate = useNavigate();
  const theme = CHANNEL_THEME[channel] || CHANNEL_THEME.whatsapp;
  const Icon = theme.Icon;

  const openAgent = (e) => {
    e.stopPropagation();
    if (agentId) navigate(`/agents-flow/canvas/${agentId}`);
  };

  return (
    <div
      className="ch-card"
      style={{
        "--ch-color": theme.color,
        "--ch-icon-bg": theme.iconBg,
      }}
    >
      <div className="ch-card__left">
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} className="ch-card__avatar" />
        ) : (
          <div className="ch-card__icon">
            <Icon size={20} />
          </div>
        )}
        <div className="ch-card__info">
          <div className="ch-card__header">
            <span className="ch-card__name">{name}</span>
            <span className={`ch-status-badge ch-status-badge--${statusVariant}`}>
              {statusLabel}
            </span>
            {isPaused && (
              <span className="ch-status-badge ch-status-badge--warning">
                Pausado
              </span>
            )}
          </div>
          <div className="ch-card__meta">
            {subtitle && <span>{subtitle}</span>}
            {agentName && agentId && (
              <button
                type="button"
                className="ch-card__agent-link"
                onClick={openAgent}
                title="Abrir agente"
              >
                <IconRobot size={12} />
                <span>{agentName}</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="ch-card__actions">
        {canPause && (
          <button
            type="button"
            className={`ch-action-btn ${
              isPaused ? "ch-action-btn--success" : "ch-action-btn--warning"
            }`}
            disabled={isMutating}
            onClick={onTogglePause}
            title={isPaused ? "Reativar agente" : "Pausar agente"}
            aria-label={isPaused ? "Reativar agente" : "Pausar agente"}
          >
            {isPaused ? <IconPlayerPlay size={18} /> : <IconPlayerPause size={18} />}
          </button>
        )}
        <button
          type="button"
          className="ch-action-btn ch-action-btn--danger"
          disabled={isMutating}
          onClick={onDisconnect}
          title="Desconectar"
          aria-label="Desconectar"
        >
          {isMutating ? <Spinner animation="border" size="sm" /> : <IconTrash size={18} />}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// SUB-COMPONENT: AvailableChannelCard
// ============================================================

const AvailableChannelCard = ({ channel, isComingSoon, isMetaLocked, onClick }) => {
  const theme = CHANNEL_THEME[channel] || CHANNEL_THEME.whatsapp;
  const Icon = theme.Icon;
  const disabled = isComingSoon || isMetaLocked;

  return (
    <button
      type="button"
      className="ch-avail-card"
      style={{ "--ch-color": theme.color, "--ch-icon-bg": theme.iconBg }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <div className="ch-avail-card__icon">
        <Icon size={20} />
      </div>
      <div className="ch-avail-card__content">
        <span className="ch-avail-card__name">
          {theme.label}
          {isComingSoon && (
            <span className="ch-avail-card__coming-badge">
              <IconClock size={10} style={{ marginRight: 2 }} />
              Em breve
            </span>
          )}
          {isMetaLocked && !isComingSoon && (
            <span
              className="ch-avail-card__coming-badge"
              style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}
            >
              <IconLock size={10} style={{ marginRight: 2 }} />
              Aguardando liberação Meta
            </span>
          )}
        </span>
        <span className="ch-avail-card__desc">{theme.description}</span>
      </div>
      <div className="ch-avail-card__action">
        {isComingSoon
          ? <IconClock size={18} />
          : isMetaLocked
            ? <IconLock size={18} />
            : <IconArrowRight size={18} />}
      </div>
    </button>
  );
};

// ============================================================
// SUB-COMPONENT: ChannelsEmptyState
// ============================================================

const ChannelsEmptyState = () => (
  <div className="ch-empty">
    <IconPlug size={48} className="ch-empty__icon" />
    <h4 className="ch-empty__title">Nenhum canal conectado</h4>
    <p className="ch-empty__desc">
      Conecte seu primeiro canal de comunicação na seção abaixo
      para começar a receber mensagens dos seus clientes.
    </p>
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function Channels() {
  const navigate = useNavigate();
  const { user, setBottyData } = useContext(UserContext);

  const isMetaUnlocked = META_ALLOWED_ACCOUNTS.has(Number(user?.account_id));
  const metaLockedForUser = (channelId) =>
    META_RESTRICTED_CHANNELS.includes(channelId) && !isMetaUnlocked;

  const [whatsappAccounts, setWhatsappAccounts] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [messengerAccounts, setMessengerAccounts] = useState([]);
  const [agentsById, setAgentsById] = useState({});
  const [isLoadingWa, setIsLoadingWa] = useState(true);
  const [isLoadingIg, setIsLoadingIg] = useState(true);
  const [isLoadingMsg, setIsLoadingMsg] = useState(true);
  const [isMutating, setIsMutating] = useState(false);

  const [notify, setNotify] = useState({
    title: "",
    message: "",
    type: "",
    visible: false,
  });

  // Estados modal de conexão WA
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showWhatsappConnect, setShowWhatsappConnect] = useState(false);
  const [isGeneratingSession, setIsGeneratingSession] = useState(false);
  const [isWaitingForSession, setIsWaitingForSession] = useState(false);
  const [sessionCheckAttempts, setSessionCheckAttempts] = useState(0);

  // ---------- Load accounts ----------

  useEffect(() => {
    const loadWa = async () => {
      try {
        const response = await GetAccounts();
        if (Array.isArray(response)) {
          const processed = response
            .filter((a) => a.name)
            .map((a) => ({
              ...a,
              phone: a.me?.id?.replace("@c.us", "") || "Não disponível",
              displayName: a.me?.pushName || a.name,
            }));
          setWhatsappAccounts(processed);
        }
      } catch (err) {
        console.error("Erro ao carregar contas WhatsApp:", err);
        setWhatsappAccounts([]);
      } finally {
        setIsLoadingWa(false);
      }
    };

    const loadIg = async () => {
      try {
        const resp = await InstagramApiService.listAccounts();
        const list = (resp?.data || []).filter((a) => a.is_active);
        setInstagramAccounts(list);
      } catch (err) {
        console.error("Erro ao carregar contas Instagram:", err);
        setInstagramAccounts([]);
      } finally {
        setIsLoadingIg(false);
      }
    };

    const loadMsg = async () => {
      try {
        const resp = await MessengerApiService.listAccounts();
        const list = (resp?.data || []).filter((a) => a.is_active);
        setMessengerAccounts(list);
      } catch (err) {
        console.error("Erro ao carregar contas Messenger:", err);
        setMessengerAccounts([]);
      } finally {
        setIsLoadingMsg(false);
      }
    };

    const loadAgents = async () => {
      try {
        const resp = await AgentsFlowService.listAgents({ page_size: 200 });
        const list = resp?.data?.results || resp?.data || [];
        const byId = {};
        list.forEach((a) => {
          byId[String(a.id)] = a;
        });
        setAgentsById(byId);
      } catch (err) {
        console.error("Erro ao carregar agentes:", err);
      }
    };

    loadWa();
    loadIg();
    loadMsg();
    loadAgents();
  }, []);

  // Resolve agente vinculado a uma conta WhatsApp via agent_id ou wabotty_account match
  const resolveWaAgent = useCallback(
    (account) => {
      if (!account) return null;
      const wantedPhone = (account.phone || "").replace(/\D/g, "");
      const list = Object.values(agentsById);

      if (account.agent_id) {
        const direct = agentsById[String(account.agent_id)];
        if (direct) return direct;
      }
      // Fallback: match por wabotty_account (canonicalizado)
      return (
        list.find((a) => {
          const wa = (a.wabotty_account || "").replace(/\D/g, "");
          return wa && wa === wantedPhone;
        }) || null
      );
    },
    [agentsById],
  );

  // ---------- Helpers ----------

  const showError = useCallback((message = "Tente novamente em instantes.") => {
    setNotify({
      title: "Oops!",
      message,
      type: "error",
      visible: true,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    setNotify({
      title: "Sucesso!",
      message,
      type: "success",
      visible: true,
    });
  }, []);

  // ---------- WhatsApp handlers ----------

  const checkSessionStatus = async (phone, intervalId, timeoutId) => {
    try {
      setSessionCheckAttempts((prev) => prev + 1);
      const result = await CheckAccountStatus(phone, null);
      const status = result.status || result.data?.status;
      if (status === "WORKING" || status === "SCAN_QR_CODE") {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        Swal.close();
        setIsWaitingForSession(false);
        setShowWhatsappConnect(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Erro ao verificar status:", err);
      return false;
    }
  };

  const handleGenerateSession = async () => {
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (!phoneNumber || (cleanPhone.length !== 10 && cleanPhone.length !== 11)) {
      setNotify({
        type: "warning",
        message: "Informe um número de telefone válido (10 ou 11 dígitos).",
        title: "Atenção",
        visible: true,
      });
      return;
    }

    setIsGeneratingSession(true);
    setSessionCheckAttempts(0);

    try {
      const response = await ConnectWhatsappAccount({
        account_id: user?.account_id,
        phone: cleanPhone,
        only_connection: 1,
      });

      if (response?.status === "STARTING") {
        setIsWaitingForSession(true);
        Swal.fire({
          title: "Aguardando sessão inicializar...",
          html: `<div style="text-align: center; margin: 20px 0;">
                   <p>Aguarde enquanto a sessão do WhatsApp é iniciada.</p>
                   <p><strong>Pode levar até 2 minutos.</strong></p>
                 </div>`,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading(),
        });

        // eslint-disable-next-line prefer-const
        let intervalId;
        // eslint-disable-next-line prefer-const
        let timeoutId;
        intervalId = setInterval(
          () => checkSessionStatus(cleanPhone, intervalId, timeoutId),
          3000,
        );
        timeoutId = setTimeout(() => {
          clearInterval(intervalId);
          setIsWaitingForSession(false);
          Swal.fire({
            icon: "warning",
            title: "Timeout na inicialização",
            text: "A sessão não conseguiu inicializar em 2 minutos. Tente novamente.",
            confirmButtonColor: "#f39c12",
          });
        }, 120000);
      } else {
        showError("Erro ao iniciar sessão WhatsApp.");
      }
    } catch (err) {
      console.error("Erro ao iniciar sessão:", err);

      // O service WaBottyServices.ConnectWhatsappAccount faz `throw error.response`,
      // então `err` é o response do axios (tem `.data` e `.status`). Em outros
      // contextos pode vir o error completo — checamos os dois caminhos.
      const errorData = err?.data || err?.response?.data;
      const status = err?.status || err?.response?.status;

      if (errorData?.requires_payment) {
        // Limite de conexões excedido — modal com CTA pra contratar mais.
        const usageInfo =
          errorData.current_usage != null && errorData.total_limit != null
            ? `<p><strong>Uso atual:</strong> ${errorData.current_usage}/${errorData.total_limit} conexões</p>`
            : "";
        const extraInfo = errorData.extra_value
          ? `<p><strong>Valor por conexão adicional:</strong> R$ ${errorData.extra_value}</p>`
          : "";
        Swal.fire({
          icon: "info",
          title: "Limite de Conexões WhatsApp",
          html: `
            <div style="text-align: left;">
              <p>${errorData.error || "Você atingiu o limite de conexões do seu plano."}</p>
              ${usageInfo}
              ${extraInfo}
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Contratar Agora",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#3b82f6",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/subscriptions");
          }
        });
      } else if (status === 422) {
        setShowWhatsappConnect(true);
      } else if (errorData?.error) {
        // Mensagem específica do backend — mostrar pro usuário em vez de engolir.
        Swal.fire({
          icon: "error",
          title: "Erro ao iniciar sessão",
          text: errorData.error,
          confirmButtonColor: "#3b82f6",
        });
      } else {
        showError("Erro ao iniciar sessão WhatsApp.");
      }
    } finally {
      setIsGeneratingSession(false);
    }
  };

  const handleConnectionSuccess = async () => {
    try {
      const accountsArray = await GetAccounts();
      if (Array.isArray(accountsArray)) {
        const processed = accountsArray.map((account) => ({
          name: account.name || "default",
          status: account.status || "DISCONNECTED",
          phone: account.me?.id?.replace("@c.us", "") || "Não disponível",
          pushName: account.me?.pushName || "Usuário",
          account_id: account.account_id || null,
          agent_id: account.agent_id || null,
          receive_chats: account.status === "WORKING" ? 1 : 0,
        }));
        sessionStorage.setItem("botty_account", JSON.stringify(processed));
        setBottyData(processed);
      }
      showSuccess("WhatsApp conectado com sucesso!");
      handleCloseConnectModal();
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      console.error("Erro pós-conexão:", err);
      showError();
    }
  };

  const handleConnectionFailure = () => {
    showError("Não foi possível conectar após 3 tentativas. Tente mais tarde.");
    handleCloseConnectModal();
  };

  const handleCloseConnectModal = () => {
    setShowConnectModal(false);
    setPhoneNumber("");
    setShowWhatsappConnect(false);
    setIsGeneratingSession(false);
    setIsWaitingForSession(false);
    setSessionCheckAttempts(0);
  };

  const handleDisconnectWhatsapp = (account) => {
    const isCloudApi = account?.provider === "cloud_api";
    Swal.fire({
      title: "Desconectar WhatsApp?",
      text: isCloudApi
        ? "Você está prestes a remover a vinculação deste número Cloud API."
        : "Você está prestes a excluir esta conexão WhatsApp.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: isCloudApi ? "Desconectar" : "Excluir",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);

      const onSuccess = () => {
        sessionStorage.removeItem("botty_account");
        showSuccess(
          isCloudApi
            ? "Cloud API desvinculada com sucesso!"
            : "Conexão WhatsApp excluída com sucesso!",
        );
        setTimeout(() => window.location.reload(), 2500);
      };
      const onError = () => {
        showError("Erro ao excluir conexão.");
        setIsMutating(false);
      };

      if (isCloudApi) {
        if (!account.agent_flow_id) {
          onError();
          return;
        }
        WhatsAppCloudApiService.disconnect(account.agent_flow_id)
          .then(onSuccess)
          .catch(onError);
        return;
      }

      const payload = { uid: account.uid, unique: account.unique };
      if (
        account.status === "FAILED" ||
        account.status === "SCAN_QR_CODE"
      ) {
        payload.session_name = account.name;
      } else {
        payload.phone = account.phone;
      }

      DeleteAccount(payload)
        .then((res) => {
          if (res) onSuccess();
          else onError();
        })
        .catch(onError);
    });
  };

  const handleToggleWhatsappPause = (account) => {
    const isPausing = account.status === "WORKING";
    const isCloudApi = account?.provider === "cloud_api";

    Swal.fire({
      title: isPausing ? "Pausar agente?" : "Reativar agente?",
      html: isPausing
        ? "O agente deixará de responder automaticamente. Mensagens continuam chegando na Central de Atendimento."
        : "Reativar respostas automáticas do agente para esta conta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: isPausing ? "#d97706" : "#16a34a",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: isPausing ? "Pausar" : "Reativar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);

      const onSuccess = () => {
        if (isPausing) sessionStorage.removeItem("botty_account");
        Swal.fire({
          title: "Atualizando...",
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => Swal.showLoading(),
        });
        setTimeout(() => window.location.reload(), isPausing ? 2500 : 5000);
      };
      const onError = () => {
        showError("Erro ao alterar status.");
        setIsMutating(false);
      };

      if (isCloudApi) {
        if (!account.agent_flow_id) {
          onError();
          return;
        }
        WhatsAppCloudApiService.pauseOrActivate(
          account.agent_flow_id,
          isPausing ? 0 : 1,
        )
          .then(onSuccess)
          .catch(onError);
        return;
      }

      PauseOrActiveAccount({
        status: isPausing ? 0 : 1,
        phone: account.phone,
      })
        .then((res) => (res ? onSuccess() : onError()))
        .catch(onError);
    });
  };

  // ---------- Instagram handlers ----------

  const handleToggleInstagramPause = (igAccount) => {
    if (!igAccount?.agent_flow_id) return;
    const isPausing = !igAccount.bot_paused;
    Swal.fire({
      title: isPausing ? "Pausar agente?" : "Reativar agente?",
      html: isPausing
        ? "O agente deixará de responder DMs do Instagram. Webhooks continuam chegando na Central."
        : "Reativar respostas automáticas do agente para esta conta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: isPausing ? "#d97706" : "#16a34a",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: isPausing ? "Pausar" : "Reativar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);

      InstagramApiService.update(igAccount.agent_flow_id, {
        bot_paused: isPausing,
      })
        .then((resp) => {
          setInstagramAccounts((prev) =>
            prev.map((a) =>
              a.id === igAccount.id
                ? { ...a, bot_paused: resp?.data?.bot_paused ?? isPausing }
                : a,
            ),
          );
          showSuccess(
            isPausing
              ? "Agente Instagram pausado."
              : "Agente Instagram reativado.",
          );
          setTimeout(() => window.location.reload(), 1500);
        })
        .catch(() => showError("Erro ao alterar status."))
        .finally(() => setIsMutating(false));
    });
  };

  const handleToggleMessengerPause = (msgAccount) => {
    if (!msgAccount?.agent_flow_id) return;
    const isPausing = !msgAccount.bot_paused;
    Swal.fire({
      title: isPausing ? "Pausar agente?" : "Reativar agente?",
      html: isPausing
        ? "O agente deixará de responder DMs do Messenger. Webhooks continuam chegando na Central."
        : "Reativar respostas automáticas do agente para esta Page?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: isPausing ? "#d97706" : "#16a34a",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: isPausing ? "Pausar" : "Reativar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);
      MessengerApiService.update(msgAccount.agent_flow_id, {
        bot_paused: isPausing,
      })
        .then((resp) => {
          setMessengerAccounts((prev) =>
            prev.map((a) =>
              a.id === msgAccount.id
                ? { ...a, bot_paused: resp?.data?.bot_paused ?? isPausing }
                : a,
            ),
          );
          showSuccess(
            isPausing ? "Agente Messenger pausado." : "Agente Messenger reativado.",
          );
          setTimeout(() => window.location.reload(), 1500);
        })
        .catch(() => showError("Erro ao alterar status."))
        .finally(() => setIsMutating(false));
    });
  };

  const handleDisconnectMessenger = (msgAccount) => {
    if (!msgAccount?.agent_flow_id) return;
    Swal.fire({
      title: "Desconectar Messenger?",
      html: `Você está prestes a desconectar a Page <strong>${msgAccount.page_name}</strong>${
        msgAccount.agent_flow_name
          ? ` do agente <strong>${msgAccount.agent_flow_name}</strong>`
          : ""
      }.<br><small>Webhooks serão desativados.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Desconectar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);
      MessengerApiService.disconnect(msgAccount.agent_flow_id)
        .then(() => {
          setMessengerAccounts((prev) =>
            prev.filter((a) => a.id !== msgAccount.id),
          );
          showSuccess("Messenger desconectado.");
          setTimeout(() => window.location.reload(), 1500);
        })
        .catch(() => showError("Erro ao desconectar."))
        .finally(() => setIsMutating(false));
    });
  };

  const handleDisconnectInstagram = (igAccount) => {
    if (!igAccount?.agent_flow_id) return;
    Swal.fire({
      title: "Desconectar Instagram?",
      html: `Você está prestes a desconectar <strong>@${igAccount.username}</strong>${
        igAccount.agent_flow_name
          ? ` do agente <strong>${igAccount.agent_flow_name}</strong>`
          : ""
      }.<br><small>Webhooks serão desativados.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Desconectar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsMutating(true);
      InstagramApiService.disconnect(igAccount.agent_flow_id)
        .then(() => {
          setInstagramAccounts((prev) => prev.filter((a) => a.id !== igAccount.id));
          showSuccess("Instagram desconectado.");
          setTimeout(() => window.location.reload(), 1500);
        })
        .catch(() => showError("Erro ao desconectar."))
        .finally(() => setIsMutating(false));
    });
  };

  // ---------- Render ----------

  const isLoading = isLoadingWa || isLoadingIg || isLoadingMsg;
  const totalConnected =
    whatsappAccounts.length + instagramAccounts.length + messengerAccounts.length;

  return (
    <>
      <section className="ch-page">
        <div className="ch-page__header">
          <h1 className="ch-page__title">Canais</h1>
          <p className="ch-page__subtitle">
            Conecte seu Agente de IA em diferentes canais de comunicação
          </p>
        </div>

        {isLoading ? (
          <div className="ch-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {/* Conectados */}
            <section className="ch-section">
              <div className="ch-section__header">
                <h3 className="ch-section__title">Canais conectados</h3>
                {totalConnected > 0 && (
                  <span className="ch-section__count">· {totalConnected}</span>
                )}
              </div>

              {totalConnected === 0 ? (
                <ChannelsEmptyState />
              ) : (
                <div className="ch-card-list">
                  {/* WhatsApp primeiro */}
                  {whatsappAccounts.map((account) => {
                    const statusInfo =
                      WAHA_STATUS[account.status] || WAHA_STATUS.STOPPED;
                    const linkedAgent = resolveWaAgent(account);
                    return (
                      <ConnectedChannelCard
                        key={`wa-${account.name}`}
                        channel="whatsapp"
                        name={account.displayName || account.phone}
                        subtitle={
                          account.status === "WORKING" ? account.phone : null
                        }
                        agentId={linkedAgent?.id}
                        agentName={linkedAgent?.name}
                        statusLabel={statusInfo.label}
                        statusVariant={statusInfo.variant}
                        isPaused={false}
                        canPause={account.status === "WORKING"}
                        isMutating={isMutating}
                        onTogglePause={() => handleToggleWhatsappPause(account)}
                        onDisconnect={() => handleDisconnectWhatsapp(account)}
                      />
                    );
                  })}

                  {/* Instagram depois */}
                  {instagramAccounts.map((ig) => (
                    <ConnectedChannelCard
                      key={`ig-${ig.id}`}
                      channel="instagram"
                      avatarUrl={ig.profile_picture_url}
                      name={`@${ig.username}`}
                      agentId={ig.agent_flow_id}
                      agentName={ig.agent_flow_name}
                      statusLabel="Ativo"
                      statusVariant="success"
                      isPaused={ig.bot_paused}
                      canPause
                      isMutating={isMutating}
                      onTogglePause={() => handleToggleInstagramPause(ig)}
                      onDisconnect={() => handleDisconnectInstagram(ig)}
                    />
                  ))}

                  {/* Messenger por último */}
                  {messengerAccounts.map((msg) => (
                    <ConnectedChannelCard
                      key={`msg-${msg.id}`}
                      channel="messenger"
                      avatarUrl={msg.profile_picture_url}
                      name={msg.page_name || "Página Facebook"}
                      agentId={msg.agent_flow_id}
                      agentName={msg.agent_flow_name}
                      statusLabel="Ativo"
                      statusVariant="success"
                      isPaused={msg.bot_paused}
                      canPause
                      isMutating={isMutating}
                      onTogglePause={() => handleToggleMessengerPause(msg)}
                      onDisconnect={() => handleDisconnectMessenger(msg)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Disponíveis */}
            <section className="ch-section">
              <div className="ch-section__header">
                <h3 className="ch-section__title">Adicionar canal</h3>
              </div>

              <div className="ch-avail-grid">
                <AvailableChannelCard
                  channel="whatsapp"
                  onClick={() => setShowConnectModal(true)}
                />
                <AvailableChannelCard
                  channel="instagram"
                  isMetaLocked={metaLockedForUser('instagram')}
                  onClick={() => navigate("/agents-flow")}
                />
                <AvailableChannelCard
                  channel="messenger"
                  isMetaLocked={metaLockedForUser('messenger')}
                  onClick={() => navigate("/agents-flow")}
                />
                <AvailableChannelCard channel="website" isComingSoon />
              </div>
            </section>
          </>
        )}

        {notify.visible && (
          <AlertResponses
            title={notify.title}
            message={notify.message}
            type={notify.type}
            visible={notify.visible}
            setNotify={setNotify}
          />
        )}

        {/* Modal Conectar WhatsApp */}
        <Modal
          show={showConnectModal}
          onHide={handleCloseConnectModal}
          size="md"
          centered
          className="ch-modal"
        >
          {!showWhatsappConnect ? (
            <>
              <div className="ch-modal__header">
                <button
                  type="button"
                  className="ch-modal__close"
                  onClick={handleCloseConnectModal}
                  aria-label="Fechar"
                >
                  <IconX size={16} />
                </button>
                <div className="ch-modal__icon-wrapper">
                  <div
                    className="ch-modal__icon"
                    style={{
                      "--ch-icon-bg":
                        "linear-gradient(135deg, #25D366 0%, #1ea55a 100%)",
                    }}
                  >
                    <IconBrandWhatsapp size={28} />
                  </div>
                </div>
                <h2 className="ch-modal__title">Conectar WhatsApp</h2>
                <p className="ch-modal__subtitle">
                  Informe o número que será usado pelo agente
                </p>
              </div>
              <div className="ch-modal__body">
                <div className="ch-modal__field">
                  <label className="ch-modal__label">
                    Número do WhatsApp
                  </label>
                  <NumberFormat
                    className="ch-modal__input"
                    format={(value) => {
                      const cleanValue = value.replace(/\D/g, "");
                      if (cleanValue.length === 0) return "";
                      if (cleanValue.length <= 2) return `(${cleanValue}`;
                      if (cleanValue.length <= 6)
                        return cleanValue.replace(/(\d{2})(\d{0,4})/, "($1) $2");
                      if (cleanValue.length <= 10)
                        return cleanValue.replace(
                          /(\d{2})(\d{4})(\d{0,4})/,
                          "($1) $2-$3",
                        );
                      return cleanValue.replace(
                        /(\d{2})(\d{5})(\d{0,4})/,
                        "($1) $2-$3",
                      );
                    }}
                    mask="_"
                    placeholder="(00) 00000-0000"
                    value={phoneNumber}
                    onValueChange={(values) =>
                      setPhoneNumber(values.formattedValue)
                    }
                  />
                  <span className="ch-modal__hint">
                    Aceita fixo (10 dígitos) ou celular (11 dígitos)
                  </span>
                </div>
              </div>
              <div className="ch-modal__footer">
                <button
                  type="button"
                  className="ch-modal__btn ch-modal__btn--secondary"
                  onClick={handleCloseConnectModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="ch-modal__btn ch-modal__btn--primary"
                  disabled={
                    isGeneratingSession ||
                    !phoneNumber ||
                    (phoneNumber.replace(/\D/g, "").length !== 10 &&
                      phoneNumber.replace(/\D/g, "").length !== 11)
                  }
                  onClick={handleGenerateSession}
                >
                  {isGeneratingSession ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Iniciando...
                    </>
                  ) : (
                    "Gerar QR Code"
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="ch-modal__header">
                <button
                  type="button"
                  className="ch-modal__close"
                  onClick={handleCloseConnectModal}
                  aria-label="Fechar"
                >
                  <IconX size={16} />
                </button>
                <h2 className="ch-modal__title">Escaneie o QR Code</h2>
              </div>
              <div className="ch-modal__body">
                <WhatsappConnect
                  connectOrReconnect={{ current: "connect" }}
                  phoneNumber={phoneNumber}
                  agentId={null}
                  onConnectionSuccess={handleConnectionSuccess}
                  onConnectionFailure={handleConnectionFailure}
                  setNotify={setNotify}
                />
              </div>
            </>
          )}
        </Modal>
      </section>
    </>
  );
}
